import { create } from 'zustand';
import dummySessions from './session-dummy-data';
import useAppStore from './app';
import useDataBroker from './dataBroker';

const useSessionStore = create((set, get) => ({
  count: 0,
  initialStateLoaded: false,
  transferSessionSid: null,
  endSessionSid: null,
  focusedSession: null,
  focusedSessionLocation: null,
  focusedSessionSid: null,
  testingCount: 0,
  sessions: {},
  reconnect: {},
  waiting: {},
  review: {},
  testing: {},
  checkout: {},

  updateLoader: () =>
    set(() => {
      return {
        initialStateLoaded: true,
      };
    }),

  /**
   * Add a session to the store.
   * @function
   * @param {string} sessionSid - The session ID of the session to add.
   * @param {Object} data - The data of the session to add.
   * The data object should contain the following properties:
   * - location (string): The location of the session. Valid values are "waiting", "review", "testing", or "checkout".
   * @returns {Object} The updated store with the added session.
   */
  add: (sessionSid, data) =>
    set((state) => {
      const updatedWaiting = { ...state.waiting };
      const updatedReview = { ...state.review };
      const updatedTesting = { ...state.testing };
      const updatedCheckout = { ...state.checkout };
      const updatedReconnect = { ...state.reconnect };

      // add this to the sessions that are in the room...
      get().sessions[sessionSid] = { location: data.location };

      // Loop through items and update the waiting object
      switch (data.location) {
        case 'waiting': {
          // make a sound, but only after the intial state has been loaded...
          if (get().initialStateLoaded && useAppStore.getState().allowSounds) {
            const audio = new Audio('/assets/sounds/sign-on.mp3');
            audio.play();
          }

          updatedWaiting[sessionSid] = data;
          break;
        }
        case 'review':
          updatedReview[sessionSid] = data;
          break;
        case 'testing':
          updatedTesting[sessionSid] = data;
          break;
        case 'checkout':
          updatedCheckout[sessionSid] = data;
          break;
        case 'reconnect':
          updatedReconnect[sessionSid] = data;
          break;
        default:
          break;
      }

      return {
        waiting: updatedWaiting,
        review: updatedReview,
        testing: updatedTesting,
        checkout: updatedCheckout,
        reconnect: updatedReconnect,
        testingCount: Object.keys(updatedTesting)?.length || 0,
      };
    }),

  /**
   * Removes a session from its current location based on the sessionSid.
   *
   * @param {string} sessionSid - The session identifier for which the session needs to be removed.
   *
   * This function updates the state by removing the session from the appropriate
   * category (waiting, review, testing, or checkout) depending on its current location.
   */

  remove: (sessionSid) => {
    if (useDataBroker.getState().mode === 'remote') {
      // this will be an API call...
      //useDataBroker.getState().persist(sessionSid, {location: toState});
    } else {
      get().handleRemoveEvent(sessionSid);
    }
  },

  handleRemoveEvent: (sessionSid) =>
    set((state) => {
      const sessionState = get().sessions[sessionSid].location;

      const updatedWaiting = { ...state.waiting };
      const updatedReview = { ...state.review };
      const updatedTesting = { ...state.testing };
      const updatedCheckout = { ...state.checkout };
      const updatedReconnect = { ...state.reconnect };

      // Loop through items and update the waiting object
      switch (sessionState) {
        case 'waiting':
          delete updatedWaiting[sessionSid];
          break;
        case 'review':
          delete updatedReview[sessionSid];
          break;
        case 'testing':
          delete updatedTesting[sessionSid];
          break;
        case 'checkout':
          delete updatedCheckout[sessionSid];
          break;
        case 'reconnect':
          delete updatedReconnect[sessionSid];
          break;
        default:
          break;
      }

      return {
        waiting: updatedWaiting,
        review: updatedReview,
        testing: updatedTesting,
        checkout: updatedCheckout,
        reconnect: updatedReconnect,
      };
    }),

  /**
   *
   * THIS IS FOR TESTING ONLY..
   *
   * Adds a random session from the dummySessions data to the current session state
   * if it is not already present. It first determines which sessions are not
   * currently in the room, then randomly selects one from the remaining options,
   * and finally adds the selected session to the session store.
   */

  addRandom: () => {
    // Get all keys from sourceObject
    const sessionOptions = Object.keys(dummySessions);
    const sessionsInRoom = Object.keys(get().sessions);

    const sessionsInRoomSet = new Set(sessionsInRoom); // Convert arr2 to a Set for O(1) lookup time
    const sessionsRemaining = sessionOptions.filter(
      (value) => !sessionsInRoomSet.has(value)
    );

    const shuffled = [...sessionsRemaining]; // Copy the original array to avoid mutation
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // Pick a random index
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
    }

    const randomSessionSid = shuffled.shift();

    get().add(randomSessionSid, dummySessions[randomSessionSid]);
  },

  getSessionData: (sessionSid) => {
    const location = get().sessions?.[sessionSid]?.location;

    if (location) {
      const sessionData = get()[location]?.[sessionSid];
      return sessionData;
    } else {
      console.error(`Session location not found for session ID: ${sessionSid}`);
      return null;
    }
  },

  setTransferSessionSid: (sessionSid) => {
    set({ transferSessionSid: sessionSid });
  },

  addSessionMediaConnectionBundle: (sessionSid, bundle) => {
    set((state) => {
      
      const sessions = { ...state.sessions };

      console.log(sessionSid);
      console.log(sessions);
      console.log(bundle);

      sessions[sessionSid].mediaConnectionBundle = bundle;

      return {
        sessions: sessions,
      };
    });
  },

  getSessionMediaConnectionBundle: (sessionSid) => {

      const bundle = get().sessions[sessionSid]?.mediaConnectionBundle;

      return bundle;

  },

  transferSession: async () => {
    const sessionSid = get().transferSessionSid;
    // handle transfer....

    // now remove session....
    get().remove(sessionSid);
    set({ transferSessionSid: null });

    // only do this if the session was in testing.
    if (useAppStore.getState()[sessionSid]?.location === 'testing') {
      useAppStore.getState().calculateLayoutSizing();
    }
  },

  setEndSessionSid: (sessionSid) => {
    set({ endSessionSid: sessionSid });
  },

  endSession: async () => {
    // handle transfer....
    const sessionSid = get().endSessionSid;
    // now remove session....
    set({ endSessionSid: null });
    get().remove(sessionSid);

    // only do this if the session was in testing.
    if (useAppStore.getState()[sessionSid]?.location === 'testing') {
      useAppStore.getState().calculateLayoutSizing();
    }
  },

  clearFocusedSession: () => {
    set(() => ({ focusedSessionSid: null, focusedSession: null }));
    useAppStore.getState().setAllowSounds(true);
  },

  getFocusedSessionSid: () => get().focusedSessionSid,

  transitionSession: (sessionSid, toState) => {
    if (useDataBroker.getState().mode === 'remote') {
      useDataBroker.getState().persist(sessionSid, { location: toState });
    } else {
      get().handleTransitionEvent(sessionSid, toState);
    }
  },

  handleTransitionEvent: (sessionSid, toState) => {
    set((state) => {
      const states = {
        waiting: { ...state.waiting },
        review: { ...state.review },
        testing: { ...state.testing },
        checkout: { ...state.checkout },
        reconnect: { ...state.reconnect },
      };

      const sessions = { ...state.sessions };

      const location = get().sessions?.[sessionSid]?.location;
      const sessionData = get()[location]?.[sessionSid];

      // remove from current location
      sessionData.location = toState;
      sessions[sessionSid].location = toState;

      states[toState][sessionSid] = sessionData;
      delete states[location][sessionSid];

      //TODO: Event emitters here maybe......

      return {
        waiting: states.waiting,
        review: states.review,
        testing: states.testing,
        checkout: states.checkout,
        reconnect: states.reconnect,
        sessions: sessions,
      };
    });
  },

  setFocusedSession: (sessionSid) => {
    const location = get().sessions?.[sessionSid]?.location;
    const sessionData = get()[location]?.[sessionSid];

    // If the focused session is currently in the waiting stage, we need to move them to in review area...
    if (location === 'waiting') {
      get().transitionSession(sessionSid, 'review');
    }

    set({
      focusedSession: sessionData,
      focusedSessionSid: sessionSid,
    });
    useAppStore.getState().setAllowSounds(false);
  },
  getFocusedSession: () => {
    const sessionSid = get().focusedSessionSid;

    const location = get().sessions?.[sessionSid]?.location;
    const sessionData = get()[location]?.[sessionSid];
    return sessionData;
  },

  persistChange: async () => {},
}));

window.store = useSessionStore;
export default useSessionStore;
